/**
 * Kaydedilemeyen atomik satışların yerel kuyruğu (çökme / sayfa kapanması kurtarması).
 *
 * Eskiden tek bir anahtar (`gts_pending_atomic_payment`) vardı: bir sonraki ödeme onu
 * eziyordu ve oturum kapatılınca siliniyordu → kaydedilemeyen satış iz bırakmadan
 * kayboluyordu. Artık her satış kendi IdempotencyKey'i ile ayrı bir yuvada durur ve
 * oturum kapatılınca SİLİNMEZ. Eski tek yuva okunmaya devam eder (geriye uyum) ve
 * ilk okumada yeni yapıya taşınır.
 *
 * Kayıtlar firma kimliğiyle saklanır; başka firmanın oturumunda tekrar gönderilmez.
 */
import { PENDING_ATOMIC_PAYMENT_KEY, PENDING_ATOMIC_PAYMENTS_STORE_KEY } from './accessLockError';

export interface PendingAtomicPayment {
  /** IdempotencyKey (yoksa yerel üretilmiş anahtar) */
  key: string;
  /** ör. process-cash-with-invoice */
  endpoint: string;
  request: any;
  timestamp: number;
  /** Kaydın yazıldığı firma (eski tek yuvadan taşınanlarda bilinmez → null) */
  companyId?: string | null;
}

/** Aynı tarayıcıda en fazla bu kadar bekleyen kayıt tutulur (en eskiler düşer). */
const MAX_SLOTS = 30;

const readStore = (): Record<string, PendingAtomicPayment> => {
  try {
    const raw = localStorage.getItem(PENDING_ATOMIC_PAYMENTS_STORE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeStore = (store: Record<string, PendingAtomicPayment>): void => {
  try {
    const entries = Object.values(store)
      .filter((e) => e && typeof e.key === 'string')
      .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0))
      .slice(-MAX_SLOTS);
    if (entries.length === 0) {
      localStorage.removeItem(PENDING_ATOMIC_PAYMENTS_STORE_KEY);
      return;
    }
    const next: Record<string, PendingAtomicPayment> = {};
    for (const e of entries) next[e.key] = e;
    localStorage.setItem(PENDING_ATOMIC_PAYMENTS_STORE_KEY, JSON.stringify(next));
  } catch (err) {
    console.warn('[localStorage] Bekleyen ödeme kaydı yazılamadı:', err);
  }
};

/**
 * Eski tek yuvadaki kaydı (varsa) yeni yapıya taşır. Eski kod bu yuvayı oturum
 * kapanınca sildiği için içindeki kayıt bu oturumun (= şu anki firmanın) kaydıdır.
 */
const migrateLegacySlot = (currentCompanyId: string): void => {
  try {
    const raw = localStorage.getItem(PENDING_ATOMIC_PAYMENT_KEY);
    if (!raw) return;
    const legacy = JSON.parse(raw);
    if (legacy && legacy.endpoint && legacy.request) {
      const key: string = legacy.request.IdempotencyKey || `legacy_${legacy.timestamp || Date.now()}`;
      const store = readStore();
      if (!store[key]) {
        store[key] = {
          key,
          endpoint: legacy.endpoint,
          request: legacy.request,
          timestamp: legacy.timestamp || Date.now(),
          companyId: legacy.companyId ?? currentCompanyId,
        };
        writeStore(store);
      }
    }
    localStorage.removeItem(PENDING_ATOMIC_PAYMENT_KEY);
  } catch {
    /* bozuk eski kayıt: dokunma, okuma tarafı yine dener */
  }
};

/**
 * Satış isteği sunucuya gitmeden HEMEN ÖNCE çağrılır. Döndürülen anahtar
 * başarıdan sonra `removePendingAtomicPayment` ile silinir.
 */
export const savePendingAtomicPayment = (
  endpoint: string,
  request: any,
  companyId?: string | null
): string => {
  const key: string = request?.IdempotencyKey || `local_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  try {
    const store = readStore();
    store[key] = { key, endpoint, request, timestamp: Date.now(), companyId: companyId ?? null };
    writeStore(store);
  } catch (err) {
    console.warn('[localStorage] Kayıt başarısız:', err);
  }
  return key;
};

/** Satış kaydedildi (ya da kalıcı olarak kurtarılamaz) → yuvayı sil. */
export const removePendingAtomicPayment = (key: string | null | undefined): void => {
  if (!key) return;
  try {
    const store = readStore();
    if (store[key]) {
      delete store[key];
      writeStore(store);
    }
    // Eski tek yuvada aynı satış duruyorsa onu da temizle
    const raw = localStorage.getItem(PENDING_ATOMIC_PAYMENT_KEY);
    if (raw) {
      const legacy = JSON.parse(raw);
      if (legacy?.request?.IdempotencyKey === key) localStorage.removeItem(PENDING_ATOMIC_PAYMENT_KEY);
    }
  } catch {
    /* yoksay */
  }
};

/**
 * Bu firmaya ait bekleyen kayıtlar (en eski önce). Eski tek yuva önce taşınır.
 * Başka firmanın kayıtları döndürülmez (o firmanın oturumunda kurtarılır).
 */
export const listPendingAtomicPayments = (currentCompanyId: string): PendingAtomicPayment[] => {
  migrateLegacySlot(currentCompanyId);
  return Object.values(readStore())
    .filter((e) => (e.companyId ?? currentCompanyId) === currentCompanyId)
    .sort((a, b) => (a.timestamp || 0) - (b.timestamp || 0));
};

/**
 * Aynı kaydın iki kez eşzamanlı yeniden gönderilmesini önler (bileşen yeniden
 * bağlanırsa kurtarma akışı ikinci kez başlamasın). Sunucu aynı anahtarı zaten
 * tekilleştirir; bu yalnızca çift pencereyi önler.
 */
const recoveryInFlight = new Set<string>();
export const tryBeginPendingRecovery = (key: string): boolean => {
  if (recoveryInFlight.has(key)) return false;
  recoveryInFlight.add(key);
  return true;
};
export const endPendingRecovery = (key: string): void => {
  recoveryInFlight.delete(key);
};
